import { createApi } from '@reduxjs/toolkit/query/react';

// Utils
import { HttpMethod, createBaseQuery, BaseQueryArgs } from '../base';
import { optimisticUpdatePlan, optimisticClearPlanContact, optimisticDeletePlanAccount, optimisticLinkPlanToContact } from './utils/optimistic-updates';
import { CACHE_TAGS, TAG_TYPES, createPlanningCacheUtils } from './utils/cache-utils';

// Types
import { Account, Plan } from '@/types/planning';
import { ApiResponse } from '../types';

type PlanWithFetchTime = Plan & { _updatedAt: number };

const customBaseQuery = async (args: string | BaseQueryArgs, api: any, extraOptions: any) => {
  const startedAt = Date.now();
  const result = await createBaseQuery()(args, api, extraOptions);
  result.meta = { ...(result.meta || {}), startedAt };
  return result;
};

/**
 * Cache Strategy: Stale-While-Revalidate
 *
 * This API is configured to serve cached data immediately while fetching fresh data in the background:
 * - keepUnusedDataFor: 600s (20 minutes) - Cache persists longer for immediate serving
 * - refetchOnMountOrArgChange: 30s - Background refetch if data is older than 30 seconds
 * - refetchOnFocus: true - Refetch when user returns to the tab
 * - refetchOnReconnect: true - Refetch when network reconnects
 *
 * This provides the best user experience: instant data display + fresh data when possible
 */

// API Endpoints
export const ENDPOINTS = {
  GET_PLAN: '/plans/:planId/',
  UPDATE_PLAN: '/plans/:planId/',
  CLEAR_PLAN_CONTACT: '/plans/:planId/contact',
  GET_PLAN_FOR_CONTACT: '/contacts/:contactId/plan',
  GET_PLAN_ACCOUNT: '/plans/:planId/accounts/:accountId/',
  DELETE_PLAN_ACCOUNT: '/plans/:planId/accounts/:accountId/',
} as const;

export const planningPlanAccountsApi = createApi({
  reducerPath: 'planningPlanAccountsApi',
  baseQuery: customBaseQuery,
  tagTypes: TAG_TYPES,
  refetchOnMountOrArgChange: 30,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  endpoints: builder => ({
    getPlan: builder.query<PlanWithFetchTime, string>({
      query: planId => ENDPOINTS.GET_PLAN.replace(':planId', planId),
      providesTags: (_result, _error, planId) => [CACHE_TAGS.PLAN(planId)],
      keepUnusedDataFor: 1200, // 20 minutes
      transformResponse: (response: ApiResponse<Plan>, meta): PlanWithFetchTime => {
        const startTime = (meta as any)?.startedAt || Date.now();
        return { ...response.data, _updatedAt: startTime };
      },
      merge: (current, incoming) => {
        if (incoming._updatedAt < current._updatedAt) return current; // stale
        return incoming;
      },
    }),

    getPlanForContact: builder.query<PlanWithFetchTime, string>({
      query: contactId => ENDPOINTS.GET_PLAN_FOR_CONTACT.replace(':contactId', contactId),
      providesTags: (result, _error, _contactId) => [...(result && result._id ? [CACHE_TAGS.PLAN(result._id)] : [])],
      keepUnusedDataFor: 1200, // 20 minutes
      transformResponse: (response: ApiResponse<Plan>, meta): PlanWithFetchTime => {
        const startTime = (meta as any)?.startedAt || Date.now();
        return { ...response.data, _updatedAt: startTime };
      },
      merge: (current, incoming) => {
        if (incoming._updatedAt < current._updatedAt) return current; // stale
        return incoming;
      },
    }),

    getPlanAccount: builder.query<Account, { planId: string; accountId: string }>({
      query: ({ planId, accountId }) => ENDPOINTS.GET_PLAN_ACCOUNT.replace(':planId', planId).replace(':accountId', accountId),
      providesTags: (_result, _error, { accountId }) => [CACHE_TAGS.ACCOUNT(accountId)],
      keepUnusedDataFor: 1200, // 20 minutes
      transformResponse: (response: ApiResponse<Account>) => response.data,
    }),

    updatePlan: builder.mutation<Plan, { planId: string; contactId: string; payload: Partial<Plan> }>({
      query: ({ planId, payload }) => ({
        url: ENDPOINTS.UPDATE_PLAN.replace(':planId', planId),
        method: HttpMethod.PATCH,
        body: payload,
      }),
      transformResponse: (response: ApiResponse<Plan>) => response.data,
      onQueryStarted: async ({ planId, contactId, payload }, { dispatch, queryFulfilled }) => {
        // Optimistically update both plan cache and contact plan cache
        const patches = optimisticUpdatePlan({
          dispatch,
          api: planningPlanAccountsApi,
          planId,
          contactId,
          payload,
        });

        try {
          await queryFulfilled;
        } catch {
          patches.forEach(patch => patch.undo());
        }
      },
    }),

    clearPlanContact: builder.mutation<Plan, { planId: string; contactId?: string }>({
      query: ({ planId }) => ({
        url: ENDPOINTS.CLEAR_PLAN_CONTACT.replace(':planId', planId),
        method: HttpMethod.DELETE,
      }),
      onQueryStarted: async ({ planId, contactId }, { dispatch, queryFulfilled }) => {
        const patches = optimisticClearPlanContact({
          dispatch,
          api: planningPlanAccountsApi,
          planId,
          contactId,
        });

        try {
          await queryFulfilled;
        } catch {
          patches.forEach(patch => patch.undo());
        }
      },
      transformResponse: (response: ApiResponse<Plan>) => response.data,
    }),

    deletePlanAccount: builder.mutation<void, { planId: string; accountId: string; contactId?: string }>({
      query: ({ planId, accountId }) => ({
        url: ENDPOINTS.DELETE_PLAN_ACCOUNT.replace(':planId', planId).replace(':accountId', accountId),
        method: HttpMethod.DELETE,
      }),
      invalidatesTags: (_result, _error, { accountId }) => [CACHE_TAGS.ACCOUNT(accountId)],
      onQueryStarted: async ({ planId, accountId, contactId }, { dispatch, queryFulfilled }) => {
        const patches = optimisticDeletePlanAccount({
          dispatch,
          api: planningPlanAccountsApi,
          planId,
          accountId,
          contactId,
        });

        try {
          await queryFulfilled;
        } catch (error) {
          patches.forEach(patch => patch.undo());
        }
      },
    }),

    linkPlanToContact: builder.mutation<Plan, { planId: string; contactId?: string; previousPlanId?: string }>({
      query: ({ planId, contactId }) => ({
        url: ENDPOINTS.UPDATE_PLAN.replace(':planId', planId),
        method: HttpMethod.PATCH,
        body: { contactId },
      }),
      onQueryStarted: async ({ planId, contactId }, { dispatch, queryFulfilled }) => {
        const patches = optimisticLinkPlanToContact({
          dispatch,
          api: planningPlanAccountsApi,
          planId,
          contactId,
        });

        try {
          await queryFulfilled;
        } catch {
          patches.forEach(patch => patch.undo());
        }
      },
      transformResponse: (response: ApiResponse<Plan>) => response.data,
    }),
  }),
});

export const {
  useGetPlanQuery,
  useGetPlanForContactQuery,
  useUpdatePlanMutation,
  useClearPlanContactMutation,
  useDeletePlanAccountMutation,
  useLinkPlanToContactMutation,
} = planningPlanAccountsApi;

export default planningPlanAccountsApi;
export const planningCacheUtils = createPlanningCacheUtils(planningPlanAccountsApi);
